// src/App.tsx
import React, { useState, useEffect } from 'react';
import Question from './Question';
import Result from './Result';
import { QuestionData } from './types';

export const Quiz: React.FC = () => {
  const [quizQuestions, setQuizQuestions] = useState<QuestionData[]>(quizData);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | null>(null);

  useEffect(() => {
    fetch('/quizData.json')
      .then((response) => response.json())
      .then((data) => setQuizQuestions(data));
  }, []);

  const handleAnswerSelect = (index: number) => {
    setSelectedAnswers((prevSelected) =>
      prevSelected.includes(index)
        ? prevSelected.filter((i) => i !== index)
        : [...prevSelected, index]
    );
  };

  const handleSubmitAnswer = () => {
    const correctAnswers = quizQuestions[currentQuestion].options
      .map((option, index) => (option.isCorrect ? index : null))
      .filter((index): index is number => index !== null);

    const isCorrect =
      correctAnswers.every((index) => selectedAnswers.includes(index)) &&
      selectedAnswers.every((index) => correctAnswers.includes(index));

    setIsAnswerCorrect(isCorrect);

    if (isCorrect) setScore((prevScore) => prevScore + 1);

    setTimeout(() => {
      setSelectedAnswers([]);
      setIsAnswerCorrect(null);

      const nextQuestion = currentQuestion + 1;
      if (nextQuestion < quizQuestions.length) {
        setCurrentQuestion(nextQuestion);
      } else {
        setShowResult(true);
      }
    }, 1500);
  };

  if (quizQuestions.length === 0) return <p>Loading quiz...</p>;

  return (
    <div className="app">
      <h1>AWS Cloud Practitioner Quiz</h1>
      {showResult ? (
        <Result score={score} totalQuestions={quizQuestions.length} />
      ) : (
        <Question
          question={quizQuestions[currentQuestion]}
          handleAnswerSelect={handleAnswerSelect}
          selectedAnswers={selectedAnswers}
          handleSubmitAnswer={handleSubmitAnswer}
          isAnswerCorrect={isAnswerCorrect}
        />
      )}
    </div>
  );
};


const quizData = [
  {
    "question": "Which of the following are benefits of cloud computing?",
    "options": [
      { "answerText": "Increased capital expenditure", "isCorrect": false },
      { "answerText": "Scalability", "isCorrect": true },
      { "answerText": "Reduced security", "isCorrect": false },
      { "answerText": "Pay-as-you-go pricing", "isCorrect": true },
      { "answerText": "Manual provisioning", "isCorrect": false }
    ]
  },
  {
    "question": "What is the primary purpose of Amazon RDS?",
    "options": [
      { "answerText": "File storage", "isCorrect": false },
      { "answerText": "Relational database management", "isCorrect": true },
      { "answerText": "Application hosting", "isCorrect": false },
      { "answerText": "Load balancing", "isCorrect": false }
    ]
  },
  {
    "question": "Which AWS service allows users to run a containerized application without managing the underlying infrastructure?",
    "options": [
      { "answerText": "Amazon ECS", "isCorrect": true },
      { "answerText": "AWS Lambda", "isCorrect": false },
      { "answerText": "Amazon EKS", "isCorrect": true },
      { "answerText": "Amazon RDS", "isCorrect": false }
    ]
  },
  {
    "question": "What is the purpose of AWS IAM?",
    "options": [
      { "answerText": "To provide network security", "isCorrect": false },
      {
        "answerText": "To manage user access and permissions",
        "isCorrect": true
      },
      { "answerText": "To encrypt data", "isCorrect": false },
      { "answerText": "To provide billing support", "isCorrect": false }
    ]
  },
  {
    "question": "Which AWS service is primarily used for data warehousing?",
    "options": [
      { "answerText": "Amazon S3", "isCorrect": false },
      { "answerText": "Amazon Redshift", "isCorrect": true },
      { "answerText": "AWS Lambda", "isCorrect": false },
      { "answerText": "Amazon CloudFront", "isCorrect": false }
    ]
  },
  {
    "question": "What does EC2 stand for?",
    "options": [
      { "answerText": "Elastic Cloud Compute", "isCorrect": false },
      { "answerText": "Elastic Compute Cloud", "isCorrect": true },
      { "answerText": "Enhanced Compute Cloud", "isCorrect": false },
      { "answerText": "Elastic Cloud Container", "isCorrect": false }
    ]
  },
  {
    "question": "Which of the following are serverless compute services?",
    "options": [
      { "answerText": "AWS Lambda", "isCorrect": true },
      { "answerText": "Amazon EC2", "isCorrect": false },
      { "answerText": "AWS Fargate", "isCorrect": true },
      { "answerText": "Amazon S3", "isCorrect": false }
    ]
  },
  {
    "question": "What is Amazon S3 primarily used for?",
    "options": [
      { "answerText": "Relational database storage", "isCorrect": false },
      { "answerText": "Object storage", "isCorrect": true },
      { "answerText": "Load balancing", "isCorrect": false },
      { "answerText": "Content delivery", "isCorrect": true }
    ]
  },
  {
    "question": "What is the purpose of Amazon CloudFront?",
    "options": [
      { "answerText": "To store data", "isCorrect": false },
      { "answerText": "To distribute content globally", "isCorrect": true },
      { "answerText": "To provide database services", "isCorrect": false },
      { "answerText": "To provide compute resources", "isCorrect": false }
    ]
  },
  {
    "question": "Which AWS service is used for monitoring and management?",
    "options": [
      { "answerText": "AWS IAM", "isCorrect": false },
      { "answerText": "Amazon CloudWatch", "isCorrect": true },
      { "answerText": "Amazon S3", "isCorrect": false },
      { "answerText": "Amazon Redshift", "isCorrect": false }
    ]
  },
  {
    "question": "Which of the following services provide database options?",
    "options": [
      { "answerText": "Amazon RDS", "isCorrect": true },
      { "answerText": "AWS DynamoDB", "isCorrect": true },
      { "answerText": "Amazon CloudFront", "isCorrect": false },
      { "answerText": "AWS Lambda", "isCorrect": false }
    ]
  },
  {
    "question": "What is the main benefit of using Amazon CloudFront?",
    "options": [
      { "answerText": "Increased storage capacity", "isCorrect": false },
      { "answerText": "Reduced latency for global users", "isCorrect": true },
      { "answerText": "Increased compute resources", "isCorrect": false },
      { "answerText": "Improved database performance", "isCorrect": false }
    ]
  },
  {
    "question": "Which AWS service provides a fully managed NoSQL database?",
    "options": [
      { "answerText": "Amazon RDS", "isCorrect": false },
      { "answerText": "AWS DynamoDB", "isCorrect": true },
      { "answerText": "Amazon S3", "isCorrect": false },
      { "answerText": "Amazon Redshift", "isCorrect": false }
    ]
  },
  {
    "question": "Which of the following services supports MySQL, PostgreSQL, and SQL Server?",
    "options": [
      { "answerText": "Amazon Redshift", "isCorrect": false },
      { "answerText": "Amazon RDS", "isCorrect": true },
      { "answerText": "AWS Lambda", "isCorrect": false },
      { "answerText": "AWS DynamoDB", "isCorrect": false }
    ]
  },
  {
    "question": "Which AWS service can be used to automate the deployment of infrastructure?",
    "options": [
      { "answerText": "AWS IAM", "isCorrect": false },
      { "answerText": "AWS CloudFormation", "isCorrect": true },
      { "answerText": "Amazon S3", "isCorrect": false },
      { "answerText": "AWS Lambda", "isCorrect": false }
    ]
  },
  {
    "question": "Which of these are components of AWS' Well-Architected Framework?",
    "options": [
      { "answerText": "Cost Optimization", "isCorrect": true },
      { "answerText": "Scalability", "isCorrect": false },
      { "answerText": "Operational Excellence", "isCorrect": true },
      { "answerText": "Fault Tolerance", "isCorrect": false }
    ]
  },
  {
    "question": "What type of storage is Amazon S3?",
    "options": [
      { "answerText": "Block storage", "isCorrect": false },
      { "answerText": "File storage", "isCorrect": false },
      { "answerText": "Object storage", "isCorrect": true },
      { "answerText": "Relational storage", "isCorrect": false }
    ]
  },
  {
    "question": "Which AWS service is used to migrate databases to AWS?",
    "options": [
      { "answerText": "AWS Data Pipeline", "isCorrect": false },
      { "answerText": "AWS Database Migration Service", "isCorrect": true },
      { "answerText": "Amazon S3", "isCorrect": false },
      { "answerText": "AWS Lambda", "isCorrect": false }
    ]
  },
  {
    "question": "What is the purpose of AWS Route 53?",
    "options": [
      { "answerText": "DNS management", "isCorrect": true },
      { "answerText": "Database hosting", "isCorrect": false },
      { "answerText": "Compute management", "isCorrect": false },
      { "answerText": "Storage management", "isCorrect": false }
    ]
  },
  {
    "question": "Which of the following can help to reduce the cost of EC2 instances?",
    "options": [
      { "answerText": "Use On-Demand instances", "isCorrect": false },
      { "answerText": "Use Reserved Instances", "isCorrect": true },
      { "answerText": "Use Spot Instances", "isCorrect": true },
      { "answerText": "Increase instance size", "isCorrect": false }
    ]
  }
]